import { useRef, useState } from 'react'
import { MousePointerClick } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import type { FieldType, FormDefinition, FormField, FormPage } from '../../types'
import FieldInspector from './FieldInspector'
import FieldList from './FieldList'
import PageList from './PageList'
import {
    addField, addPage, allFieldKeys, duplicateField, keyFromLabel, moveField, movePage, removeField, removePage,
    replaceField, uniqueKey, updateField, updatePage,
} from './model'

export interface Selection {
    pageId: string | null
    index: number | null
}

interface Props {
    def: FormDefinition
    edit: (fn: (d: FormDefinition) => FormDefinition) => void
    selection: Selection
    setSelection: (s: Selection) => void
    publishedKeys: Set<string>
    publishedVersion?: string
    problemKeys: Set<string>
    /** Bumped from outside (problem click) to focus the inspector label. */
    externalFocus?: number
}

/** Three panes: pages | fields of the selected page | inspector for the selected field. */
export default function DesignTab({ def, edit, selection, setSelection, publishedKeys, publishedVersion, problemKeys, externalFocus = 0 }: Props) {
    // Fields whose key still follows the label (new in this session and untouched), as "pageId|key".
    const autoKeys = useRef(new Set<string>())
    const [focusToken, setFocusToken] = useState(0)

    const page: FormPage | undefined = def.pages.find((p) => p.id === selection.pageId) ?? def.pages[0]
    const pageId = page?.id ?? null
    const index = page && selection.index !== null && selection.index < page.fields.length ? selection.index : null
    const field: FormField | undefined = page && index !== null ? page.fields[index] : undefined
    const ak = (k: string) => `${pageId}|${k}`

    const select = (p: string | null, i: number | null) => setSelection({ pageId: p, index: i })

    const onAddPage = () => {
        const r = addPage(def)
        edit((d) => addPage(d).def)
        select(r.pageId, null)
    }
    const onRemovePage = (id: string) => {
        const i = def.pages.findIndex((p) => p.id === id)
        const next = def.pages.filter((p) => p.id !== id)
        edit((d) => removePage(d, id))
        select(next[Math.min(i, next.length - 1)]?.id ?? null, null)
    }
    const onAddField = (type: FieldType) => {
        if (!pageId) return
        const r = addField(def, pageId, type)
        edit((d) => addField(d, pageId, type).def)
        autoKeys.current.add(`${pageId}|${r.key}`)
        select(pageId, r.index)
        setFocusToken((n) => n + 1)
    }
    const onDuplicate = (i: number) => {
        if (!pageId) return
        edit((d) => duplicateField(d, pageId, i).def)
        select(pageId, i + 1)
        setFocusToken((n) => n + 1)
    }
    const onRemoveField = (i: number) => {
        if (!pageId || !page) return
        autoKeys.current.delete(ak(page.fields[i].key))
        edit((d) => removeField(d, pageId, i))
        const remaining = page.fields.length - 1
        select(pageId, remaining === 0 ? null : index === null ? null : i < index ? index - 1 : i === index ? Math.min(i, remaining - 1) : index)
    }
    const onMoveField = (i: number, delta: number) => {
        if (!pageId) return
        edit((d) => moveField(d, pageId, i, delta))
        if (index === i) select(pageId, i + delta)
        else if (index === i + delta) select(pageId, i)
    }

    const otherKeys = (own: string) => {
        const keys = allFieldKeys(def)
        keys.delete(own)
        return keys
    }
    const onLabel = (label: string) => {
        if (!pageId || index === null || !field) return
        if (autoKeys.current.has(ak(field.key))) {
            const key = uniqueKey(keyFromLabel(label, field.type === 'html' ? 'info' : 'new_field'), otherKeys(field.key))
            autoKeys.current.delete(ak(field.key))
            autoKeys.current.add(ak(key))
            edit((d) => updateField(d, pageId, index, { label, key }))
        } else {
            edit((d) => updateField(d, pageId, index, { label }))
        }
    }
    const onKey = (key: string) => {
        if (!pageId || index === null || !field) return
        autoKeys.current.delete(ak(field.key))
        edit((d) => updateField(d, pageId, index, { key }))
    }

    return (
        <div className="grid gap-4 lg:h-[calc(100vh-17rem)] lg:min-h-[34rem] lg:grid-cols-[18rem_minmax(0,1fr)_26rem] 2xl:grid-cols-[20rem_minmax(0,1fr)_32rem]">
            <div className="lg:overflow-y-auto lg:px-1">
            <PageList
                def={def}
                selectedId={pageId}
                onSelect={(id) => select(id, null)}
                onAdd={onAddPage}
                onUpdate={(id, patch) => edit((d) => updatePage(d, id, patch))}
                onRemove={onRemovePage}
                onMove={(id, delta) => edit((d) => movePage(d, id, delta))}
            />
            </div>
            <div className="lg:overflow-y-auto lg:px-1">
                {page ? (
                    <FieldList
                        page={page}
                        selectedIndex={index}
                        problemKeys={problemKeys}
                        onSelect={(i) => select(pageId, i)}
                        onAdd={onAddField}
                        onDuplicate={onDuplicate}
                        onRemove={onRemoveField}
                        onMove={onMoveField}
                    />
                ) : (
                    <Empty className="border border-dashed">
                        <EmptyHeader>
                            <EmptyTitle>This form has no pages</EmptyTitle>
                            <EmptyDescription>Add a page on the left to start designing.</EmptyDescription>
                        </EmptyHeader>
                    </Empty>
                )}
            </div>
            <Card size="sm" className="lg:max-h-full lg:overflow-y-auto">
                <CardHeader><CardTitle>Field settings</CardTitle></CardHeader>
                <CardContent>
                    {field && pageId && index !== null ? (
                        <FieldInspector
                            def={def}
                            pageId={pageId}
                            index={index}
                            field={field}
                            publishedKeys={publishedKeys}
                            publishedVersion={publishedVersion}
                            focusToken={focusToken + externalFocus}
                            onPatch={(patch) => edit((d) => updateField(d, pageId, index, patch))}
                            onReplace={(f) => edit((d) => replaceField(d, pageId, index, f))}
                            onLabel={onLabel}
                            onKey={onKey}
                        />
                    ) : (
                        <Empty>
                            <EmptyHeader>
                                <EmptyMedia variant="icon"><MousePointerClick /></EmptyMedia>
                                <EmptyTitle>No field selected</EmptyTitle>
                                <EmptyDescription>Pick a field in the middle to edit its label, options and visibility.</EmptyDescription>
                            </EmptyHeader>
                        </Empty>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
